/**
 * @kgc/partners - Partner DTOs with Zod validation
 * Epic 7: Story 7-1: Partner CRUD
 *
 * UpdatePartnerSchema convention (M-017 resolution):
 *   - `.nullable().optional()` — DB-nullable fields where caller can explicitly set null to clear value
 *   - `.optional()` only — non-nullable DB fields with defaults (omit = don't change, null is invalid)
 */

import { z } from 'zod';

// ============================================
// ENUM SCHEMAS
// ============================================

export const PartnerTypeSchema = z.enum(['INDIVIDUAL', 'COMPANY']);
export const PartnerStatusSchema = z.enum([
  'ACTIVE',
  'INACTIVE',
  'BLACKLISTED',
  'TRUSTED',
  'WARNING',
]);

// ============================================
// CREATE PARTNER DTO
// ============================================

export const CreatePartnerSchema = z.object({
  /** Partner típus */
  type: PartnerTypeSchema.default('INDIVIDUAL'),

  /** Partner kód (egyedi tenant-en belül) */
  partnerCode: z
    .string()
    .min(1, { message: 'A partner kód kötelező' })
    .max(50, { message: 'A partner kód maximum 50 karakter' }),

  /** Név */
  name: z
    .string()
    .min(1, { message: 'A név kötelező' })
    .max(255, { message: 'A név maximum 255 karakter' }),

  /** Cégnév (cégeknél) */
  companyName: z.string().max(255).optional(),

  /** Kapcsolattartó neve */
  contactName: z.string().max(255).optional(),

  /** Adószám */
  taxNumber: z
    .string()
    .max(20)
    .regex(/^[0-9-]+$/, { message: 'Érvénytelen adószám formátum' })
    .optional(),

  /** EU VAT szám */
  euVatNumber: z.string().max(20).optional(),

  /**
   * Külső rendszer azonosítója (ADR-145) — a bejövő web-igény visszacsatolási kulcsa.
   * A kettő EGYÜTT jelent linket: `externalSource` nélkül két forrás azonos kulcsa ütközne.
   */
  externalId: z.string().max(128).optional(),

  /** Melyik rendszerből jött a partner (`rpi-web`, később más). */
  externalSource: z.string().max(64).optional(),

  /** Email */
  email: z.string().email({ message: 'Érvénytelen email cím' }).optional(),

  /** Értesítési email (FG-1-2) */
  notificationEmail: z.string().email({ message: 'Érvénytelen email cím' }).optional(),

  /** Számlázási email (FG-1-2) */
  billingEmail: z.string().email({ message: 'Érvénytelen email cím' }).optional(),

  /** Telefon */
  phone: z.string().max(30).optional(),

  /** Másodlagos telefon */
  phoneAlt: z.string().max(30).optional(),

  /** Ország (ISO kód) */
  country: z.string().length(2, { message: 'Ország kód 2 karakter' }).optional(),

  /** Irányítószám */
  postalCode: z.string().max(20).optional(),

  /** Város */
  city: z.string().max(100).optional(),

  /** Cím */
  address: z.string().max(255).optional(),

  /** Másodlagos cím */
  addressAlt: z.string().max(255).optional(),

  /** Születési dátum */
  birthDate: z.coerce.date().optional(),

  /** Születési hely (jogi célra, E-55 Story 55-5) */
  birthPlace: z.string().max(100).optional(),

  /** Anyja neve (magyar jogi azonosító, FG-1-1) */
  mothersName: z.string().max(255).optional(),

  /** Személyi igazolvány szám */
  idCardNumber: z.string().max(50).optional(),

  /** Jogosítvány szám */
  drivingLicenseNo: z.string().max(50).optional(),

  /** Tartózkodási hely (FG-1-4: ideiglenes cím, ha eltér a lakcímtől) */
  temporaryAddress: z.string().max(500).optional(),

  /** Tartózkodási hely érvényessége (FG-1-4) */
  tempAddressValidUntil: z.coerce.date().optional(),

  /** Utolsó okmány beolvasás dátuma (FG-1-3) */
  lastDocumentScanDate: z.coerce.date().optional(),

  /** Hitelkeret */
  creditLimit: z.number().int().min(0).optional(), // M-021: .int() — HUF integer szabály

  /** Fizetési határidő (napokban) */
  paymentTermDays: z.number().int().min(0).max(365).default(0),

  /** Alapértelmezett kedvezmény % — M-009 fix: .int() egész százalék */
  defaultDiscountPc: z
    .number()
    .int({ message: 'Kedvezmény egész szám kell legyen' })
    .min(0)
    .max(100)
    .default(0),

  /** Bérlési kedvezmény % (O-PRT-1, max 15%) — M-009 fix: .int() */
  discountRental: z
    .number()
    .int({ message: 'Kedvezmény egész szám kell legyen' })
    .min(0)
    .max(15, { message: 'Maximum 15% kedvezmény engedélyezett' })
    .optional(),

  /** Vásárlási kedvezmény % (O-PRT-1, max 15%) — M-009 fix: .int() */
  discountSales: z
    .number()
    .int({ message: 'Kedvezmény egész szám kell legyen' })
    .min(0)
    .max(15, { message: 'Maximum 15% kedvezmény engedélyezett' })
    .optional(),

  /** Szerviz kedvezmény % (O-PRT-1, max 15%) — M-009 fix: .int() */
  discountService: z
    .number()
    .int({ message: 'Kedvezmény egész szám kell legyen' })
    .min(0)
    .max(15, { message: 'Maximum 15% kedvezmény engedélyezett' })
    .optional(),

  /** Vezetői bypass (FG-3-3) — ADMIN/OWNER only */
  paymentBypassEnabled: z.boolean().optional(),

  /** Figyelmeztetés megjegyzés */
  warningNote: z.string().max(500).optional(),

  /** Megjegyzések */
  notes: z.string().optional(),
});

export type CreatePartnerInput = z.infer<typeof CreatePartnerSchema>;

// ============================================
// UPDATE PARTNER DTO
// ============================================

export const UpdatePartnerSchema = z.object({
  type: PartnerTypeSchema.optional(),
  status: PartnerStatusSchema.optional(),
  name: z.string().min(1).max(255).optional(),
  companyName: z.string().max(255).nullable().optional(),
  contactName: z.string().max(255).nullable().optional(),
  taxNumber: z.string().max(20).nullable().optional(),
  euVatNumber: z.string().max(20).nullable().optional(),
  email: z.string().email().nullable().optional(),
  notificationEmail: z.string().email().nullable().optional(),
  billingEmail: z.string().email().nullable().optional(),
  phone: z.string().max(30).nullable().optional(),
  phoneAlt: z.string().max(30).nullable().optional(),
  country: z.string().length(2).nullable().optional(),
  postalCode: z.string().max(20).nullable().optional(),
  city: z.string().max(100).nullable().optional(),
  address: z.string().max(255).nullable().optional(),
  addressAlt: z.string().max(255).nullable().optional(),
  birthDate: z.coerce.date().nullable().optional(),
  birthPlace: z.string().max(100).nullable().optional(),
  mothersName: z.string().max(255).nullable().optional(),
  idCardNumber: z.string().max(50).nullable().optional(),
  drivingLicenseNo: z.string().max(50).nullable().optional(),
  temporaryAddress: z.string().max(500).nullable().optional(),
  tempAddressValidUntil: z.coerce.date().nullable().optional(),
  lastDocumentScanDate: z.coerce.date().nullable().optional(),
  creditLimit: z.number().int().min(0).nullable().optional(), // M-021: .int() — HUF integer szabály
  paymentTermDays: z.number().int().min(0).max(365).optional(),
  defaultDiscountPc: z.number().int().min(0).max(100).optional(),
  // discountRental/Sales/Service: DB non-nullable (Decimal @default(0)), null is invalid
  discountRental: z
    .number()
    .int()
    .min(0)
    .max(15, { message: 'Maximum 15% kedvezmény engedélyezett' })
    .optional(),
  discountSales: z
    .number()
    .int()
    .min(0)
    .max(15, { message: 'Maximum 15% kedvezmény engedélyezett' })
    .optional(),
  discountService: z
    .number()
    .int()
    .min(0)
    .max(15, { message: 'Maximum 15% kedvezmény engedélyezett' })
    .optional(),
  paymentBypassEnabled: z.boolean().optional(),
  warningNote: z.string().max(500).nullable().optional(),
  notes: z.string().nullable().optional(),
});

export type UpdatePartnerInput = z.infer<typeof UpdatePartnerSchema>;

// ============================================
// BLACKLIST PARTNER DTO
// ============================================

export const BlacklistPartnerSchema = z.object({
  /** Feketelistázás indoka */
  reason: z
    .string()
    .min(10, { message: 'Az indoklás legalább 10 karakter' })
    .max(500, { message: 'Az indoklás maximum 500 karakter' }),
});

export type BlacklistPartnerInput = z.infer<typeof BlacklistPartnerSchema>;

// ============================================
// UPDATE CREDIT LIMIT DTO
// ============================================

export const UpdateCreditLimitSchema = z.object({
  /** Új hitelkeret */
  creditLimit: z.number().int().min(0, { message: 'A hitelkeret nem lehet negatív' }), // M-021: .int() — HUF integer szabály

  /** Megjegyzés a változáshoz */
  reason: z.string().max(200).optional(),
});

export type UpdateCreditLimitInput = z.infer<typeof UpdateCreditLimitSchema>;

// ============================================
// SEARCH PARTNER DTO
// ============================================

export const SearchPartnerSchema = z.object({
  /** Keresési kifejezés (név, kód, telefon, email) */
  query: z.string().min(2, { message: 'Minimum 2 karakter szükséges' }),

  /** Partner típus szűrő */
  type: PartnerTypeSchema.optional(),

  /** Csak aktív partnerek */
  activeOnly: z.boolean().default(true),

  /** Maximum találatok */
  limit: z.number().int().min(1).max(50).default(10),
});

export type SearchPartnerInput = z.infer<typeof SearchPartnerSchema>;

// ============================================
// PARTNER PHONE DTO (FG-3-1)
// ============================================

// M2 client feedback: label is now free-text (max 20 chars, matching Prisma VarChar(20))
// instead of a strict enum. Preset suggestions: PRIVATE, COMPANY, FAX, OTHER.
export const PHONE_LABEL_PRESETS = ['PRIVATE', 'COMPANY', 'FAX', 'OTHER'] as const;
export const PhoneLabelSchema = z.string().min(1).max(20);
export type PhoneLabel = z.infer<typeof PhoneLabelSchema>;

export const CreatePartnerPhoneSchema = z.object({
  label: PhoneLabelSchema,
  number: z.string().min(1).max(30),
  isPrimary: z.boolean().default(false),
});

export type CreatePartnerPhoneInput = z.infer<typeof CreatePartnerPhoneSchema>;

export const UpdatePartnerPhoneSchema = z.object({
  label: PhoneLabelSchema.optional(),
  number: z.string().min(1).max(30).optional(),
  isPrimary: z.boolean().optional(),
});

export type UpdatePartnerPhoneInput = z.infer<typeof UpdatePartnerPhoneSchema>;

// ============================================
// UPDATE PARTNER STATUS DTO (E-55, Story 55-2)
// ============================================

export const UpdatePartnerStatusSchema = z
  .object({
    /** Új státusz */
    status: PartnerStatusSchema,

    /** Figyelmeztetés oka (WARNING státusz esetén kötelező) */
    warningNote: z.string().max(500).optional(),
  })
  .refine(data => data.status !== 'WARNING' || (data.warningNote && data.warningNote.length >= 5), {
    message: 'WARNING státuszhoz legalább 5 karakteres indoklás szükséges',
    path: ['warningNote'],
  });

export type UpdatePartnerStatusInput = z.infer<typeof UpdatePartnerStatusSchema>;

// ============================================
// SET COST BEARER DTO (E-55, Story 55-3)
// ============================================

export const SetCostBearerSchema = z.object({
  /** Költségviselő partner ID (null = eltávolítás) */
  costBearerId: z.string().uuid().nullable(),
});

export type SetCostBearerInput = z.infer<typeof SetCostBearerSchema>;
